import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { RefreshCw } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

interface CreateUserForm {
  email: string
  first_name: string
  last_name: string
  display_name: string
  password: string
  role: string
  language: string
  timezone: string
}

interface CreateUserDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  form: CreateUserForm
  onFormChange: (form: CreateUserForm) => void
  onSubmit: () => void
  isLoading: boolean
}

export function CreateUserDialog({
  open,
  onOpenChange,
  form,
  onFormChange,
  onSubmit,
  isLoading,
}: CreateUserDialogProps) {
  const { t } = useTranslation()

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('admin.users.createUser')}</DialogTitle>
          <DialogDescription>{t('admin.users.createUserDescription')}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="create_email">{t('admin.users.email')}</Label>
            <Input
              id="create_email"
              type="email"
              value={form.email}
              onChange={e => onFormChange({ ...form, email: e.target.value })}
            />
          </div>
          <div>
            <Label htmlFor="create_first_name">{t('admin.users.firstName')}</Label>
            <Input
              id="create_first_name"
              value={form.first_name}
              onChange={e => onFormChange({ ...form, first_name: e.target.value })}
            />
          </div>
          <div>
            <Label htmlFor="create_last_name">{t('admin.users.lastName')}</Label>
            <Input
              id="create_last_name"
              value={form.last_name}
              onChange={e => onFormChange({ ...form, last_name: e.target.value })}
            />
          </div>
          <div>
            <Label htmlFor="create_display_name">{t('admin.users.displayName')}</Label>
            <Input
              id="create_display_name"
              value={form.display_name}
              onChange={e => onFormChange({ ...form, display_name: e.target.value })}
            />
          </div>
          <div>
            <Label htmlFor="create_password">{t('admin.users.password')}</Label>
            <Input
              id="create_password"
              type="password"
              value={form.password}
              onChange={e => onFormChange({ ...form, password: e.target.value })}
              placeholder={t('admin.users.passwordPlaceholder')}
            />
          </div>
          <div>
            <Label htmlFor="create_role">{t('admin.users.role')}</Label>
            <Select
              value={form.role}
              onValueChange={(value: string) => onFormChange({ ...form, role: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder={t('admin.users.selectRole')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="super_admin">{t('admin.users.roleSuperAdmin')}</SelectItem>
                <SelectItem value="admin">{t('admin.users.roleAdmin')}</SelectItem>
                <SelectItem value="user">{t('admin.users.roleUser')}</SelectItem>
                <SelectItem value="viewer">{t('admin.users.roleViewer')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>
            {t('admin.users.cancel')}
          </Button>
          <Button onClick={onSubmit} disabled={isLoading}>
            {isLoading ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : null}
            {t('admin.users.create')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
